<template>
  <div class="pag-empresa-asignacion">
    <!-- CABECERA -->
    <div class="cabecera">
      <div class="cabecera__izquierda">
        <div class="cabecera__icono">
          <i class="bi bi-building-check"></i>
        </div>
        <div>
          <h1 class="cabecera__titulo">Módulos por Empresa</h1>
          <p class="cabecera__subtitulo">Activa o desactiva el acceso a módulos para cada empresa</p>
        </div>
      </div>
    </div>

    <!-- SELECTOR DE EMPRESA -->
    <div class="panel-selector">
      <div class="panel-selector__grupo">
        <label class="etiqueta-campo">Empresa</label>
        <div class="panel-selector__control">
          <div class="campo-busqueda-empresa">
            <i class="bi bi-search campo-busqueda-empresa__icono"></i>
            <select
              v-model="empresaId"
              @change="cargarModulosEmpresa"
              class="campo-busqueda-empresa__select"
            >
              <option value="">— Selecciona una empresa —</option>
              <option v-for="emp in empresas" :key="emp.id" :value="emp.id">
                {{ emp.business_name || emp.razon_social }} — {{ emp.ruc }}
              </option>
            </select>
          </div>
          <div class="panel-selector__acciones" v-if="empresaId">
            <button class="btn-secundario btn-sm" @click="seleccionarTodos" title="Activar todos los módulos">
              <i class="bi bi-check2-all"></i>
              Activar todos
            </button>
            <button class="btn-secundario btn-sm" @click="deseleccionarTodos" title="Desactivar todos los módulos">
              <i class="bi bi-x-circle"></i>
              Desactivar todos
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ESTADO: SIN EMPRESA SELECCIONADA -->
    <div v-if="!empresaId" class="estado-vacio">
      <i class="bi bi-building estado-vacio__icono"></i>
      <p>Selecciona una empresa para ver y gestionar sus módulos</p>
      <span class="estado-vacio__sugerencia">Los cambios aplican únicamente a la empresa seleccionada</span>
    </div>

    <!-- ESTADO: CARGANDO -->
    <div v-else-if="cargando" class="estado-carga">
      <div class="spinner"></div>
      <span>Cargando módulos de la empresa...</span>
    </div>

    <!-- ESTADO: ERROR -->
    <div v-else-if="error" class="estado-error">
      <i class="bi bi-exclamation-triangle-fill"></i>
      {{ error }}
    </div>

    <!-- CONTENIDO -->
    <template v-else-if="modulosSistema.length">
      <!-- Empresa seleccionada + resumen -->
      <div class="empresa-banner" v-if="empresaActiva">
        <div class="empresa-banner__info">
          <div class="empresa-banner__avatar">
            <i class="bi bi-building-fill"></i>
          </div>
          <div>
            <span class="empresa-banner__nombre">{{ empresaActiva.business_name || empresaActiva.razon_social }}</span>
            <span class="empresa-banner__ruc">RUC: {{ empresaActiva.ruc }}</span>
          </div>
        </div>
        <div class="empresa-banner__estadisticas">
          <div class="empresa-banner__stat">
            <span class="empresa-banner__stat-num empresa-banner__stat-num--azul">{{ totalActivos }}</span>
            <span class="empresa-banner__stat-label">Activos</span>
          </div>
          <div class="empresa-banner__stat">
            <span class="empresa-banner__stat-num">{{ totalDisponibles }}</span>
            <span class="empresa-banner__stat-label">Disponibles</span>
          </div>
          <div class="empresa-banner__barra">
            <div
              class="empresa-banner__barra-relleno"
              :style="{ width: totalDisponibles ? (totalActivos / totalDisponibles * 100) + '%' : '0%' }"
            ></div>
          </div>
        </div>
      </div>

      <!-- GRUPOS -->
      <div class="grupos">
        <div
          v-for="principal in modulosSistema"
          :key="principal.id"
          class="grupo"
        >
          <!-- Encabezado del grupo -->
          <div class="grupo__header">
            <div class="grupo__icono">
              <i :class="['bi', principal.icono || 'bi-puzzle']"></i>
            </div>
            <span class="grupo__nombre">{{ principal.nombre }}</span>
            <span class="grupo__badge">
              {{ principal.submodulos.filter(s => modulosActivos.has(s.id)).length }}
              / {{ principal.submodulos.length }}
            </span>
            <div class="grupo__acciones">
              <button class="btn-mini" @click="toggleGrupo(principal, true)" title="Activar todos">
                <i class="bi bi-check-all"></i>
              </button>
              <button class="btn-mini" @click="toggleGrupo(principal, false)" title="Desactivar todos">
                <i class="bi bi-dash-circle"></i>
              </button>
            </div>
          </div>

          <!-- Sub-módulos -->
          <div class="grupo__submodulos">
            <label
              v-for="sub in principal.submodulos"
              :key="sub.id"
              class="submodulo"
              :class="{ 'submodulo--activo': modulosActivos.has(sub.id) }"
            >
              <input
                type="checkbox"
                :checked="modulosActivos.has(sub.id)"
                @change="toggleSubmodulo(sub.id)"
                class="submodulo__check"
              />
              <div class="submodulo__body">
                <span class="submodulo__nombre">{{ sub.nombre }}</span>
                <span v-if="sub.ruta_modulo_secundario" class="submodulo__ruta">
                  {{ sub.ruta_modulo_secundario }}
                </span>
              </div>
              <i class="bi bi-check-circle-fill submodulo__icono-check"></i>
            </label>
          </div>
        </div>
      </div>
    </template>

    <!-- MÓDULOS VACÍOS -->
    <div v-else class="estado-vacio">
      <i class="bi bi-exclamation-circle estado-vacio__icono"></i>
      <p>No hay módulos disponibles. Verifica la configuración del sistema.</p>
    </div>

    <!-- BARRA DE GUARDAR -->
    <transition name="deslizar">
      <div class="barra-guardar" v-if="empresaId && hayCambios">
        <div class="barra-guardar__info">
          <i class="bi bi-info-circle"></i>
          <span>Tienes cambios sin guardar</span>
        </div>
        <div class="barra-guardar__acciones">
          <button class="btn-secundario btn-sm" @click="descartarCambios" :disabled="guardando">
            Descartar
          </button>
          <button class="btn-primario" @click="guardarCambios" :disabled="guardando">
            <span v-if="guardando" class="mini-spinner"></span>
            <i v-else class="bi bi-floppy"></i>
            Guardar cambios
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import api from '@/services/api'
import notificationService from '@/services/notificationService'

export default {
  name: 'ModulosEmpresaAsignacion',

  data() {
    return {
      empresas: [],
      empresaId: '',
      modulosSistema: [],
      modulosActivos: new Set(),
      modulosActivosOriginal: new Set(),
      cargando: false,
      guardando: false,
      error: '',
    }
  },

  computed: {
    empresaActiva() {
      return this.empresas.find(e => e.id == this.empresaId) || null
    },

    totalDisponibles() {
      return this.modulosSistema.reduce((acc, mp) => acc + (mp.submodulos?.length || 0), 0)
    },

    totalActivos() {
      return this.modulosActivos.size
    },

    hayCambios() {
      if (this.modulosActivos.size !== this.modulosActivosOriginal.size) return true
      for (const id of this.modulosActivos) {
        if (!this.modulosActivosOriginal.has(id)) return true
      }
      return false
    },
  },

  mounted() {
    this.cargarEmpresas()
  },

  methods: {
    async cargarEmpresas() {
      try {
        const { data } = await api.get('/admin/empresas')
        this.empresas = data?.data || data?.empresas || []
      } catch (e) {
        notificationService.error('No se pudieron cargar las empresas')
        console.error('[ModulosEmpresaAsignacion]', e)
      }
    },

    async cargarModulosEmpresa() {
      if (!this.empresaId) {
        this.modulosSistema = []
        this.modulosActivos = new Set()
        this.modulosActivosOriginal = new Set()
        return
      }
      this.cargando = true
      this.error = ''
      try {
        const { data } = await api.get(`/admin/empresas/${this.empresaId}/modulos`)
        const lista = data?.data || []
        this.modulosSistema = lista

        const activos = new Set()
        for (const mp of lista) {
          for (const sub of mp.submodulos || []) {
            if (sub.activo_empresa) activos.add(sub.id)
          }
        }
        this.modulosActivos = activos
        this.modulosActivosOriginal = new Set(activos)
      } catch (e) {
        this.error = 'Error al cargar los módulos de la empresa'
        console.error('[ModulosEmpresaAsignacion]', e)
      } finally {
        this.cargando = false
      }
    },

    toggleSubmodulo(id) {
      const nuevo = new Set(this.modulosActivos)
      if (nuevo.has(id)) nuevo.delete(id)
      else nuevo.add(id)
      this.modulosActivos = nuevo
    },

    toggleGrupo(principal, activar) {
      const nuevo = new Set(this.modulosActivos)
      for (const sub of principal.submodulos || []) {
        if (activar) nuevo.add(sub.id)
        else nuevo.delete(sub.id)
      }
      this.modulosActivos = nuevo
    },

    seleccionarTodos() {
      const todos = new Set()
      for (const mp of this.modulosSistema) {
        for (const sub of mp.submodulos || []) todos.add(sub.id)
      }
      this.modulosActivos = todos
    },

    deseleccionarTodos() {
      this.modulosActivos = new Set()
    },

    descartarCambios() {
      this.modulosActivos = new Set(this.modulosActivosOriginal)
    },

    async guardarCambios() {
      this.guardando = true
      try {
        await api.put(`/admin/empresas/${this.empresaId}/modulos`, {
          modulos: Array.from(this.modulosActivos),
        })
        notificationService.success('Módulos actualizados correctamente')
        await this.cargarModulosEmpresa()
      } catch (e) {
        notificationService.error('Error al guardar los cambios')
        console.error('[ModulosEmpresaAsignacion]', e)
      } finally {
        this.guardando = false
      }
    },
  },
}
</script>

<style scoped>
.pag-empresa-asignacion {
  --primario: var(--primary-main, #0ea5e9);
  --primario-hover: var(--primary-hover, #0284c7);
  --fondo: var(--bg-main, #f8fafc);
  --fondo-tarjeta: var(--secondary-bg-main, #ffffff);
  --texto: var(--text-primary, #0f172a);
  --texto-sec: var(--text-secondary, #475569);
  --texto-mut: var(--text-muted, #94a3b8);
  --borde: var(--border-main, #e2e8f0);
  --borde-foco: var(--border-focus, #0ea5e9);
  --exito: var(--accent-success, #10b981);
  --alerta: var(--accent-warning, #f59e0b);
  --peligro: var(--accent-danger, #ef4444);
  --sombra-sm: var(--shadow-sm, 0 1px 2px 0 rgb(0 0 0 / 0.05));
  --sombra-md: var(--shadow-md, 0 4px 6px -1px rgb(0 0 0 / 0.1));
  min-height: 100vh;
  padding: 1.5rem;
  padding-bottom: 6rem;
  background: var(--fondo);
  color: var(--texto);
}

/* CABECERA */
.cabecera { display: flex; justify-content: space-between; align-items: center; gap: 1rem; margin-bottom: 1.5rem; flex-wrap: wrap; }
.cabecera__izquierda { display: flex; align-items: center; gap: 0.875rem; }
.cabecera__icono { width: 2.75rem; height: 2.75rem; border-radius: 10px; background: var(--primario); color: #fff; display: flex; align-items: center; justify-content: center; font-size: 1.25rem; flex-shrink: 0; }
.cabecera__titulo { margin: 0; font-size: 1.375rem; font-weight: 700; color: var(--texto); line-height: 1.2; }
.cabecera__subtitulo { margin: 0.125rem 0 0; font-size: 0.8125rem; color: var(--texto-sec); }

/* BOTONES */
.btn-primario { display: inline-flex; align-items: center; gap: 0.375rem; padding: 0.5rem 1rem; background: var(--primario); color: #fff; border: none; border-radius: 8px; font-size: 0.875rem; font-weight: 500; cursor: pointer; transition: background 0.15s, transform 0.1s; }
.btn-primario:hover:not(:disabled) { background: var(--primario-hover); transform: translateY(-1px); }
.btn-primario:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-secundario { display: inline-flex; align-items: center; gap: 0.375rem; padding: 0.5rem 1rem; background: var(--fondo-tarjeta); color: var(--texto-sec); border: 1px solid var(--borde); border-radius: 8px; font-size: 0.875rem; font-weight: 500; cursor: pointer; transition: background 0.15s; }
.btn-secundario:hover:not(:disabled) { background: var(--borde); color: var(--texto); }
.btn-sm { padding: 0.375rem 0.75rem; font-size: 0.8125rem; }
.btn-mini { width: 1.75rem; height: 1.75rem; border: 1px solid var(--borde); border-radius: 6px; background: transparent; color: var(--texto-mut); cursor: pointer; display: inline-flex; align-items: center; justify-content: center; font-size: 0.8125rem; transition: background 0.15s, color 0.15s; }
.btn-mini:hover { background: var(--borde); color: var(--texto-sec); }

/* PANEL SELECTOR */
.panel-selector { background: var(--fondo-tarjeta); border: 1px solid var(--borde); border-radius: 10px; padding: 1.125rem; margin-bottom: 1.25rem; box-shadow: var(--sombra-sm); }
.panel-selector__grupo { display: flex; flex-direction: column; gap: 0.5rem; }
.etiqueta-campo { font-size: 0.8125rem; font-weight: 600; color: var(--texto-sec); }
.panel-selector__control { display: flex; gap: 0.75rem; flex-wrap: wrap; align-items: center; }
.campo-busqueda-empresa { position: relative; flex: 1; min-width: 260px; }
.campo-busqueda-empresa__icono { position: absolute; left: 0.75rem; top: 50%; transform: translateY(-50%); color: var(--texto-mut); pointer-events: none; font-size: 0.875rem; }
.campo-busqueda-empresa__select { width: 100%; padding: 0.625rem 0.75rem 0.625rem 2.25rem; border: 1px solid var(--borde); border-radius: 8px; background: var(--fondo); color: var(--texto); font-size: 0.875rem; cursor: pointer; appearance: auto; }
.campo-busqueda-empresa__select:focus { outline: none; border-color: var(--borde-foco); box-shadow: 0 0 0 3px rgb(14 165 233 / 0.15); }
.panel-selector__acciones { display: flex; gap: 0.5rem; flex-wrap: wrap; }

/* EMPRESA BANNER */
.empresa-banner { display: flex; justify-content: space-between; align-items: center; gap: 1.5rem; background: var(--fondo-tarjeta); border: 1px solid var(--borde); border-left: 4px solid var(--primario); border-radius: 10px; padding: 1rem 1.25rem; margin-bottom: 1.25rem; box-shadow: var(--sombra-sm); flex-wrap: wrap; }
.empresa-banner__info { display: flex; align-items: center; gap: 0.875rem; }
.empresa-banner__avatar { width: 2.5rem; height: 2.5rem; border-radius: 8px; background: rgb(14 165 233 / 0.1); color: var(--primario); display: flex; align-items: center; justify-content: center; font-size: 1.125rem; flex-shrink: 0; }
.empresa-banner__nombre { display: block; font-weight: 600; color: var(--texto); font-size: 0.9375rem; }
.empresa-banner__ruc { display: block; font-size: 0.8125rem; color: var(--texto-sec); font-family: monospace; }
.empresa-banner__estadisticas { display: flex; align-items: center; gap: 1.5rem; flex-wrap: wrap; }
.empresa-banner__stat { display: flex; flex-direction: column; align-items: center; }
.empresa-banner__stat-num { font-size: 1.375rem; font-weight: 700; color: var(--texto); line-height: 1; }
.empresa-banner__stat-num--azul { color: var(--primario); }
.empresa-banner__stat-label { font-size: 0.6875rem; color: var(--texto-mut); text-transform: uppercase; letter-spacing: 0.04em; margin-top: 0.25rem; }
.empresa-banner__barra { width: 120px; height: 8px; background: var(--borde); border-radius: 4px; overflow: hidden; }
.empresa-banner__barra-relleno { height: 100%; background: var(--primario); border-radius: 4px; transition: width 0.4s ease; }

/* GRUPOS */
.grupos { display: grid; gap: 1rem; }

.grupo { background: var(--fondo-tarjeta); border: 1px solid var(--borde); border-radius: 10px; overflow: hidden; box-shadow: var(--sombra-sm); transition: box-shadow 0.15s; }
.grupo:hover { box-shadow: var(--sombra-md); }

.grupo__header { display: flex; align-items: center; gap: 0.75rem; padding: 0.875rem 1rem; background: rgb(14 165 233 / 0.03); border-bottom: 1px solid var(--borde); }
.grupo__icono { width: 2rem; height: 2rem; border-radius: 8px; background: rgb(14 165 233 / 0.1); color: var(--primario); display: flex; align-items: center; justify-content: center; font-size: 1rem; flex-shrink: 0; }
.grupo__nombre { flex: 1; font-size: 0.9375rem; font-weight: 600; color: var(--texto); }
.grupo__badge { font-size: 0.8125rem; font-weight: 600; color: var(--primario); padding: 0.1875rem 0.625rem; background: rgb(14 165 233 / 0.1); border-radius: 20px; }
.grupo__acciones { display: flex; gap: 0.25rem; }

.grupo__submodulos { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); }

.submodulo { display: flex; align-items: flex-start; gap: 0.625rem; padding: 0.625rem 1rem; cursor: pointer; border-right: 1px solid var(--borde); border-bottom: 1px solid var(--borde); transition: background 0.1s; position: relative; }
.submodulo:hover { background: rgb(14 165 233 / 0.04); }
.submodulo--activo { background: rgb(16 185 129 / 0.04); }
.submodulo--activo .submodulo__nombre { color: var(--exito); }
.submodulo--activo .submodulo__icono-check { opacity: 1; }
.submodulo__check { margin-top: 0.125rem; accent-color: var(--exito); width: 0.9375rem; height: 0.9375rem; flex-shrink: 0; cursor: pointer; }
.submodulo__body { flex: 1; min-width: 0; }
.submodulo__nombre { display: block; font-size: 0.8125rem; font-weight: 500; color: var(--texto); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.submodulo__ruta { display: block; font-size: 0.6875rem; color: var(--texto-mut); font-family: monospace; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 0.0625rem; }
.submodulo__icono-check { font-size: 0.875rem; color: var(--exito); opacity: 0; transition: opacity 0.15s; flex-shrink: 0; margin-top: 0.0625rem; }

/* ESTADOS */
.estado-carga { display: flex; align-items: center; justify-content: center; gap: 0.75rem; padding: 4rem; color: var(--texto-sec); }
.estado-error { display: flex; align-items: center; gap: 0.625rem; padding: 1rem 1.25rem; background: rgb(239 68 68 / 0.08); border: 1px solid var(--peligro); border-radius: 8px; color: var(--peligro); font-size: 0.875rem; }
.estado-vacio { text-align: center; padding: 5rem 2rem; color: var(--texto-sec); }
.estado-vacio__icono { font-size: 3.5rem; color: var(--texto-mut); display: block; margin-bottom: 1rem; }
.estado-vacio__sugerencia { font-size: 0.8125rem; color: var(--texto-mut); margin-top: 0.5rem; display: block; }

/* BARRA GUARDAR */
.barra-guardar { position: fixed; bottom: 0; left: 0; right: 0; display: flex; justify-content: space-between; align-items: center; gap: 1rem; padding: 0.875rem 2rem; background: var(--fondo-tarjeta); border-top: 1px solid var(--borde); box-shadow: 0 -4px 16px rgb(0 0 0 / 0.08); z-index: 100; flex-wrap: wrap; }
.barra-guardar__info { display: flex; align-items: center; gap: 0.5rem; font-size: 0.875rem; color: var(--alerta); font-weight: 500; }
.barra-guardar__acciones { display: flex; gap: 0.625rem; }

/* TRANSICIÓN */
.deslizar-enter-active, .deslizar-leave-active { transition: transform 0.3s ease, opacity 0.3s ease; }
.deslizar-enter-from, .deslizar-leave-to { transform: translateY(100%); opacity: 0; }

/* SPINNER */
.spinner { width: 1.5rem; height: 1.5rem; border: 2px solid var(--borde); border-top-color: var(--primario); border-radius: 50%; animation: girar 0.7s linear infinite; }
.mini-spinner { width: 0.875rem; height: 0.875rem; border: 2px solid rgba(255,255,255,0.4); border-top-color: #fff; border-radius: 50%; animation: girar 0.6s linear infinite; display: inline-block; }

/* ANIMACIONES */
@keyframes girar { to { transform: rotate(360deg); } }
.girando { animation: girar 0.7s linear infinite; }

/* RESPONSIVE */
@media (max-width: 768px) {
  .pag-empresa-asignacion { padding: 1rem; }
  .cabecera { flex-direction: column; align-items: flex-start; }
  .grupo__submodulos { grid-template-columns: 1fr; }
  .empresa-banner { flex-direction: column; align-items: flex-start; }
  .barra-guardar { padding: 0.75rem 1rem; }
}
</style>
